/**
 * @render react
 * @name SponsorInfo
*/

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { withPolaris } from 'Components/protons/Polaris';

import { Image } from 'Components/atoms/Image';
import { Link } from 'Components/molecules/Link';

import './_style.scss';

/**
 * SponsorInfo component
 *
 * @param {Object} logo - Object containing image src and alt text
 * @param {string} introText - IntroText of the block
 * @param {string} description - description of the block
 * @param {string} url - URL of the sponsor
 * @param {string} rel - Rel attribute on the a tag
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra classes to be added to the component
 * @param {Object} context - Extra information coming from Polaris provider
 */
const SponsorInfo = (
  {
    logo,
    introText,
    description,
    url,
    rel,
    context: { cssPrefix },
    loadStyles,
    extraClassNames,
  },
) => {
  const content = (
    <>
      {introText && (
        <div className={`${cssPrefix}__sponsor-info--intro-text`}>
          {introText}
        </div>
      )}
      {(logo && logo.src) && (
        <div className={`${cssPrefix}__sponsor-info--logo`}>
          <Image
            {...logo}
            size={logo.size || 'sponsor'}
            ampLayout="fixed"
            ampDisableTransforms
            ampDensity
          />
        </div>
      )}
      {description && (
        <div className={`${cssPrefix}__sponsor-info--description`}>
          {description}
        </div>
      )}
    </>
  );

  const classes = {
    [`${cssPrefix}__sponsor-info`]: loadStyles,
    '-with-logo': (logo && logo.src),
    ...extraClassNames,
  };

  return url ? (
    <Link
      rel={rel}
      href={url}
      target="_blank"
      extraClassNames={classes}
    >
      {content}
    </Link>
  ) : (
    <div className={classNames(classes)}>
      {content}
    </div>
  );
};

/**
 * Valid props
 */
SponsorInfo.propTypes = {
  logo: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    size: PropTypes.string,
  }),
  introText: PropTypes.string,
  description: PropTypes.string,
  url: PropTypes.string,
  rel: PropTypes.string,
  context: PropTypes.shape(),
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
};

/**
 * Default props
 */
SponsorInfo.defaultProps = {
  logo: null,
  introText: null,
  description: null,
  url: null,
  rel: 'nofollow',
  context: {},
  loadStyles: true,
  extraClassNames: {},
};

export default withPolaris(SponsorInfo);
